/**
 * Slide registration management utilities.
 *
 * This module provides hooks for managing slide registration data,
 * including fetching registrations and managing selection state.
 * It handles the complex logic for determining the most relevant
 * registrations and organizing them by orchestration.
 */

import {
  getMostRelevantRegistrationPerPair,
  getRegistrationDetails,
} from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/InferenceModelsForSlides/SlideRegistrations/OrchIdRegistrations';
import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import { every, find, flatMap, forEach, groupBy, isEmpty, keyBy, map, values } from 'es-toolkit/compat';
import { useEffect, useState } from 'react';
import { CasesParams } from 'utils/useCasesParams/types';
import { useSlidesRegistrationsQuery } from './queryHooks/useSlidesRegistrationsQuery';

/**
 * Structure for organizing slide registration details by orchestration
 */
export interface SlideRegistrationDetailsByOrchestration {
  [orchestrationId: string]: RegistrationDetails[];
}

export interface RegistrationDetails {
  sourceSlideId: string;
  targetSlideId: string;
  artifactUrl: string;
}

export interface RegistrationDetailsWithStains extends RegistrationDetails {
  sourceStainingType: string;
  targetStainingType: string;
}

export const getSlidesRegistrationWithStains = (
  slides: SlideInferenceResults[],
  selectedSlideRegistrationsByOrchestration: SlideRegistrationDetailsByOrchestration
): RegistrationDetailsWithStains[] => {
  const slidesById = keyBy(slides, 'slideId');
  const flatRegistrations = flatMap(values(selectedSlideRegistrationsByOrchestration));

  const registrationDetailsWithStains: RegistrationDetailsWithStains[] = map(flatRegistrations, (registration) => {
    return {
      ...registration,
      sourceStainingType: slidesById[registration.sourceSlideId].stainingType,
      targetStainingType: slidesById[registration.targetSlideId].stainingType,
    };
  });

  return registrationDetailsWithStains;
};

const validateSlideRegistrations = (
  slideId: string,
  neighborStains: string[],
  selectedTargetStainsRegistrations: RegistrationDetailsWithStains[]
): boolean => {
  const validated = every(neighborStains, (neighborStain) => {
    const doesSlideHaveRegistrationWithNeighborStain = find(selectedTargetStainsRegistrations, (registration) => {
      return (
        (registration.sourceSlideId === slideId && registration.targetStainingType === neighborStain) ||
        (registration.targetSlideId === slideId && registration.sourceStainingType === neighborStain)
      );
    });
    return doesSlideHaveRegistrationWithNeighborStain;
  });

  return validated;
};

export const validateTargetStainRegistrations = (
  neighborStains: string[],
  slidesOfTargetStain: SlideInferenceResults[],
  selectedTargetStainsRegistrations: RegistrationDetailsWithStains[]
): boolean => {
  const slidesOfSelectedTargetStains: SlideInferenceResults[] = slidesOfTargetStain;
  const validated = every(slidesOfSelectedTargetStains, (slide) => {
    return validateSlideRegistrations(slide.slideId, neighborStains, selectedTargetStainsRegistrations);
  });

  return validated;
};

/**
 * Hook for managing slide registration data and selections.
 *
 * This hook handles fetching slide registration data when conditions are met
 * and automatically selects the most relevant registrations. It manages the
 * complex state of registration selections organized by orchestration.
 *
 * @param casesParamsForRegistrations - Parameters for fetching registrations
 * @param slideStainTypes - Array of stain types for the slides
 * @param studyId - The study identifier
 * @returns Object containing registration data, state, and management methods
 */
export const useSlideRegistrations = (
  casesParamsForRegistrations?: CasesParams,
  slideStainTypes: string[] = [],
  studyId?: string
) => {
  const [selectedSlideRegistrations, setSelectedSlideRegistration] = useState<
    SlideRegistrationDetailsByOrchestration | undefined
  >(undefined);

  // Fetch registrations data
  const {
    data: registrationsData,
    isFetching: isRegistrationsFetching,
    isError: isRegistrationsError,
    isFetched: isRegistrationsFetched,
  } = useSlidesRegistrationsQuery({ casesParamsForRegistrations, slideStainTypes });

  // Auto-select most relevant registrations when data is fetched
  useEffect(() => {
    if (
      isRegistrationsFetched &&
      !isRegistrationsFetching &&
      !isRegistrationsError &&
      isEmpty(selectedSlideRegistrations) &&
      !isEmpty(registrationsData) &&
      studyId
    ) {
      const selectedRegistrations = getMostRelevantRegistrationPerPair(registrationsData);
      const slideRegistrationsByOrchestration = groupBy(selectedRegistrations, 'orchestrationId');
      const slideRegistrationDetailsByOrchestration: SlideRegistrationDetailsByOrchestration = {};

      forEach(slideRegistrationsByOrchestration, (registrations, orchestrationId) => {
        slideRegistrationDetailsByOrchestration[orchestrationId] = map(registrations, (registration) =>
          getRegistrationDetails(registration, studyId)
        );
      });

      setSelectedSlideRegistration(slideRegistrationDetailsByOrchestration);
    }
  }, [
    registrationsData,
    isRegistrationsFetched,
    isRegistrationsFetching,
    isRegistrationsError,
    selectedSlideRegistrations,
    studyId,
  ]);

  /**
   * Clears all selected registrations.
   */
  const clearSelectedRegistrations = () => {
    setSelectedSlideRegistration(undefined);
  };

  /**
   * Updates registrations for a specific orchestration.
   *
   * @param orchestrationId - The orchestration ID to update
   * @param registrationDetails - The new registration details
   */
  const updateRegistrationForOrchestration = (orchestrationId: string, registrationDetails: any[]) => {
    setSelectedSlideRegistration((prev) => ({
      ...prev,
      [orchestrationId]: registrationDetails,
    }));
  };

  /**
   * Removes registrations for a specific orchestration.
   *
   * @param orchestrationId - The orchestration ID to remove
   */
  const removeRegistrationForOrchestration = (orchestrationId: string) => {
    setSelectedSlideRegistration((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      delete updated[orchestrationId];
      return updated;
    });
  };

  return {
    // Data
    selectedSlideRegistrations: selectedSlideRegistrations || {},

    // Actions
    setSelectedSlideRegistration,
    clearSelectedRegistrations,
    updateRegistrationForOrchestration,
    removeRegistrationForOrchestration,
  };
};
